import type { ApiClient } from '../../api/client.js';
import type { DeviceControl } from '../../api/device-controls.js';
import { faceMark } from './face-mark.js';
import { h, type View } from './dom.js';
import { createDeviceControls, reportLabel } from './device-controls.js';
import { createDeviceMembers } from './device-members.js';
import { createSavedNetworkChooser, type SavedNetworkChooser } from './wifi-saved-chooser.js';
import { createMacNetworkChooser } from './wifi-mac-chooser.js';
import type { MacWifiBridge } from '../../api/wifi-types.js';
import { getMobileBridge } from '../../platform/mobile-bridge.js';
import { deviceNetworkLimit } from '../../device-ble/provision.js';
import { agoText, describeSources, type AccountDevice, type DeviceRole, type DeviceSync, type SyncEntry, type SyncState } from '../sync/device-sync.js';

export interface DeviceSyncPanelOptions {
  api: ApiClient;
  sync: DeviceSync;
  onRename(device: AccountDevice): void;
  onRevoke(device: AccountDevice): void;
  /** ASIS One: give the unit up without revoking it (it shows a new code). Without it the button is not drawn. */
  onRelease?(device: AccountDevice): void;
  /** 'connect' runs from a click, which the browser needs to ask for a serial port. */
  openUsb(intent: 'diagnose' | 'connect'): void;
  /** false where the USB diagnostics are not mounted (native shells, browsers without serial). Default true. */
  usbDiagnostics?: boolean;
  /** Opens the shared-device invitation form. Without it the shortcut is not drawn. */
  onInvitation?(): void;
  accountName?: string;
  currentUserId?: string;
  now?: () => number;
  /** The Mac app's Keychain bridge; null hides «Usar la Wi-Fi de este Mac». Defaults to window.asisDesktop.wifi. */
  macWifi?: MacWifiBridge | null;
}

interface FormRef {
  mode: 'add' | 'configure';
  el: HTMLFormElement;
  code: HTMLInputElement | null;
  ssid: HTMLInputElement;
  psk: HTMLInputElement;
  error: HTMLElement;
  fields: HTMLElement;
  choices: HTMLElement;
  submit: HTMLButtonElement;
  /** Open «Usar una de mis redes guardadas» picker; it holds ids only, never a password. */
  saved: SavedNetworkChooser | null;
  /** Open «Usar la Wi-Fi de este Mac» picker. */
  mac: View | null;
}
interface RowRef {
  key: string;
  row: HTMLLIElement;
  name: HTMLElement; sub: HTMLElement; chips: HTMLElement; chipsKey: string; note: HTMLElement;
  actions: HTMLElement; syncActions: HTMLElement;
  rename: HTMLButtonElement; revoke: HTMLButtonElement; release: HTMLButtonElement;
  add: HTMLButtonElement; configure: HTMLButtonElement; connect: HTMLButtonElement; usb: HTMLButtonElement;
  formHost: HTMLElement; form: FormRef | null;
  advanced: HTMLDetailsElement; portrait: HTMLElement; activity: HTMLElement; info: HTMLDetailsElement; infoBody: HTMLElement; infoKey: string; control: DeviceControl | null;
  controls: View | null;
  /** Shared-device roster, and what it allowed for this unit. `role` is null until the account row exists. */
  members: View | null;
  role: DeviceRole | null;
  remoteAllowed: boolean;
  remoteNotice: HTMLElement;
  added: boolean;
  focusPending: boolean;
}

export const BOOT_HINT = 'Si tu ASIS no aparece: un ASIS One sin dueño muestra un código en su pantalla y ya está listo; espera a verlo y pulsa Sincronizar otra vez. Si tiene dueño, con el aparato encendido y en reposo, mantén BOOT 10 segundos y suelta: se abre una ventana de configuración de 5 minutos.';
const CHECKING_ACCESS = 'Comprobando acceso a los controles remotos…';
const SHARED_NO_CONTROLS = 'Los controles remotos personales no están disponibles en un dispositivo compartido.';
const UNVERIFIED_ACCESS = 'No se ha podido verificar el acceso a los controles remotos. Actualiza para reintentar.';
const MEMBER_NO_CONTROLS = 'Solo su propietario ve los controles por Wi-Fi de este aparato.';
export const NO_BLUETOOTH = 'Este navegador no permite Bluetooth. Usa ASIS para Mac o Chrome en Android. En iPhone puedes consultar tu cuenta; la configuración Bluetooth llegará con la app móvil.';

function formatDate(value: string): string { const time = Date.parse(value); return Number.isFinite(time) ? new Date(time).toLocaleString() : 'fecha no disponible'; }
function formatBytes(value: number): string { return Number.isFinite(value) && value >= 0 ? `${(value / 1024 / 1024).toFixed(1)} MB` : 'No disponible'; }

export function createDeviceSyncPanel(options: DeviceSyncPanelOptions): View {
  const { api, sync } = options;
  const clock = options.now ?? (() => Date.now());
  const rows = new Map<string, RowRef>();
  let alive = true;
  let lastRevision = -1;
  let statusText = '';
  let wasBusy = false;

  const hasMembers = typeof api.listDeviceMembers === 'function';
  const usbAvailable = options.usbDiagnostics !== false;

  const root = h('section', { class: 'card sync-card', 'aria-labelledby': 'sync-title' });
  const syncButton = h('button', { class: 'btn primary', type: 'button' }, ['Sincronizar']);
  const cancelButton = h('button', { class: 'btn ghost', type: 'button', hidden: true }, ['Cancelar']);
  const invitationButton = options.onInvitation ? h('button', { class: 'btn ghost', type: 'button' }, ['Tengo una invitación']) : null;
  const status = h('p', { class: 'sync-status', role: 'status', 'aria-live': 'polite', 'aria-atomic': 'true' });
  const alertText = h('p', { class: 'auth-error', role: 'alert', hidden: true });
  const retryButton = h('button', { class: 'btn ghost', type: 'button', hidden: true }, ['Reintentar']);
  const stepItems = { account: h('li'), ble: h('li'), usb: h('li') };
  const steps = h('ul', { class: 'sync-steps', 'aria-label': 'Estado de cada búsqueda', hidden: true }, [stepItems.account, stepItems.ble, stepItems.usb]);
  const hint = h('p', { class: 'card-sub sync-hint', hidden: true });
  const preparation = h('details', { class: 'wireless-preparation' }, [
    h('summary', {}, ['Cómo preparar tu ASIS']),
    h('div', { class: 'wireless-preparation-body' }, [h('ol', { class: 'wireless-steps', 'aria-label': 'Pasos de configuración' }, [
      h('li', {}, ['Con el aparato encendido y en reposo, mantén BOOT 10 segundos y suelta.']),
      h('li', {}, ['Pulsa Sincronizar y selecciona tu dispositivo.']),
      h('li', {}, ['Introduce tu red Wi-Fi de 2,4 GHz y comprueba la conexión.']),
    ])]),
  ]);
  const accountNote = h('p', { class: 'card-sub' }, ['La actividad indica el último contacto con el servidor. Un dispositivo en reposo puede seguir conectado al Wi-Fi. Para el uso diario basta con alimentación y Wi-Fi.']);
  const list = h('ul', { class: 'device-list', 'aria-label': 'Aparatos ASIS' });
  const placeholder = h('li', { class: 'empty' });
  const addUnit = h('button', { class: 'btn ghost', type: 'button', 'aria-expanded': 'false', 'aria-controls': 'unit-setup' }, ['Añadir unidad']);
  const setup = h('details', { id: 'unit-setup', class: 'unit-setup' }, [h('summary', {}, ['Conecta tu ASIS']),
    h('ol', { class: 'wireless-steps' }, [h('li', {}, ['Enciende tu unidad y ten a mano su código.']), h('li', {}, ['Busca tu ASIS y elige tu red Wi-Fi.'])]),
    h('div', { class: 'form-row' }, [syncButton, cancelButton]), preparation,
  ]);
  addUnit.addEventListener('click', () => { setup.open = !setup.open; addUnit.setAttribute('aria-expanded', String(setup.open)); if (setup.open) syncButton.focus(); });
  setup.addEventListener('toggle', () => { addUnit.setAttribute('aria-expanded', String(setup.open)); if (!setup.open && setup.contains(document.activeElement)) addUnit.focus(); });
  setup.addEventListener('keydown', event => { if (event.key === 'Escape') { setup.open = false; addUnit.setAttribute('aria-expanded', 'false'); addUnit.focus(); } });
  const advanced = h('details', { class: 'secondary-details sync-advanced' }, [h('summary', {}, ['Búsqueda y acceso compartido']), ...(options.accountName ? [h('p', { class: 'card-sub' }, [`Cuenta: ${options.accountName}`])] : []), accountNote, steps, ...(invitationButton ? [invitationButton] : [])]);
  root.append(h('div', { class: 'today-panel-head' }, [h('h2', { class: 'card-title', id: 'sync-title' }, ['Tus unidades']), addUnit]),
    list, alertText, retryButton, setup, status, hint, advanced);


  function ago(timestamp: number): string { return agoText(clock() - timestamp); }
  function mutate(target: HTMLElement, text: string): void { if (target.textContent !== text) target.textContent = text; }

  function createRow(entry: SyncEntry): RowRef {
    const row = h('li', { class: 'device-row', tabindex: '-1' });
    const name = h('div', { class: 'device-name' });
    const sub = h('div', { class: 'device-sub' });
    const chips = h('div', { class: 'device-chips' });
    const note = h('p', { class: 'card-sub device-note', hidden: true });
    const actions = h('div', { class: 'device-actions' });
    const syncActions = h('div', { class: 'device-sync-actions' });
    const rename = h('button', { class: 'btn ghost small', type: 'button' }, ['Renombrar']);
    const revoke = h('button', { class: 'btn ghost small danger', type: 'button' }, ['Revocar']);
    const release = h('button', { class: 'btn ghost small', type: 'button', hidden: true }, ['Liberar']);
    const add = h('button', { class: 'btn ghost small', type: 'button', hidden: true }, ['Añadir']);
    const configure = h('button', { class: 'btn ghost small', type: 'button', hidden: true }, ['Configurar Wi-Fi']);
    const connect = h('button', { class: 'btn ghost small', type: 'button', hidden: true }, ['Conectar']);
    const usb = h('button', { class: 'btn ghost small', type: 'button', hidden: true }, ['Añadir por USB']);
    const remoteNotice = h('p', { class: 'card-sub device-note', hidden: true, 'aria-live': 'polite' }, [CHECKING_ACCESS]);
    const formHost = h('div', { class: 'device-add' });
    const infoBody = h('div', { class: 'device-info-body' });
    const info = h('details', { class: 'device-info', hidden: true }, [h('summary', {}, ['Gestionar unidad']), infoBody]);
    actions.append(rename, ...(options.onRelease ? [release] : []), revoke);
    syncActions.append(add, configure, connect, usb);
    const portrait = h('span', { class: 'device-portrait' }); portrait.innerHTML = faceMark();
    const activity = h('p', { class: 'device-activity', role: 'status' });
    const advanced = info; advanced.classList.add('device-advanced', 'secondary-details'); advanced.append(actions);
    row.append(h('div', { class: 'device-main' }, [portrait, h('div', { class: 'device-identity' }, [name, activity, sub])]), chips, syncActions, note, remoteNotice, formHost, advanced);
    const ref: RowRef = { key: entry.key, row, advanced, portrait, activity, name, sub, chips, chipsKey: '', note, actions, syncActions, rename, revoke, release, add, configure, connect, usb, formHost, form: null, info, infoBody, infoKey: '', control: null, controls: null, members: null, role: null, remoteAllowed: false, remoteNotice, added: false, focusPending: false };
    const current = () => sync.getState().entries.find(item => item.key === entry.key) ?? null;
    rename.addEventListener('click', () => { const account = current()?.account; if (account) options.onRename(account); });
    revoke.addEventListener('click', () => { const account = current()?.account; if (account) options.onRevoke(account); });
    release.addEventListener('click', () => { const account = current()?.account; if (account) options.onRelease?.(account); });
    add.addEventListener('click', () => { const live = current(); if (live) openForm(ref, live, 'add'); });
    configure.addEventListener('click', () => { const live = current(); if (live) openForm(ref, live, 'configure'); });
    connect.addEventListener('click', () => { const choice = current()?.nearby?.choice; if (choice) void sync.selectChoice(choice.deviceId); });
    usb.addEventListener('click', () => options.openUsb('connect'));
    info.addEventListener('toggle', () => { const live = current(); if (live && alive) updateInfo(ref, live, sync.getState()); });
    return ref;
  }

  function closeForm(ref: RowRef): void {
    const form = ref.form;
    if (!form) return;
    form.psk.value = ''; form.ssid.value = ''; if (form.code) form.code.value = '';
    form.saved?.destroy?.(); form.saved = null;
    form.mac?.destroy?.(); form.mac = null;
    form.el.remove(); ref.form = null;
    ref.add.setAttribute('aria-expanded', 'false'); ref.configure.setAttribute('aria-expanded', 'false');
  }

  function openForm(ref: RowRef, entry: SyncEntry, mode: 'add' | 'configure'): void {
    if (ref.form || !entry.id) return;
    const id = entry.id;
    const info = entry.nearby?.info ?? null;
    const reusable = mode === 'configure' && info?.hasWifi === true;
    // ASIS One: an unowned unit shows a short code on its screen instead of needing the factory label.
    const screen = mode === 'add' && Boolean(info?.codeCheck);
    const code = !(mode === 'add') ? null : screen
      ? h('input', { class: 'input screen-code-input', type: 'text', required: true, autocomplete: 'one-time-code', autocapitalize: 'characters', maxlength: '9', spellcheck: 'false', inputmode: 'text', 'aria-label': 'Código de la pantalla de ASIS' })
      : h('input', { class: 'input', type: 'password', required: true, autocomplete: 'new-password', maxlength: '160', spellcheck: 'false', 'aria-label': 'Código privado de alta' });
    const ssid = h('input', { class: 'input', type: 'text', required: true, autocomplete: 'off', maxlength: '32', spellcheck: 'false', 'aria-label': 'Red Wi-Fi del dispositivo' });
    const psk = h('input', { class: 'input', type: 'password', autocomplete: 'new-password', maxlength: '64', 'aria-label': 'Clave Wi-Fi del dispositivo' });
    const error = h('p', { class: 'auth-error', role: 'alert', hidden: true });
    const fields = h('div', { class: 'form-stack', hidden: reusable }, [
      h('label', { class: 'field' }, [h('span', {}, ['Nombre de la red Wi-Fi de 2,4 GHz']), ssid]),
      h('label', { class: 'field' }, [h('span', {}, ['Contraseña de la red']), psk]),
      h('p', { class: 'card-sub' }, ['Deja la contraseña vacía solo si la red es abierta. ASIS la transmite cifrada al dispositivo a través de su servidor y no la conserva en tu cuenta.']),
    ]);
    const reuse = h('button', { class: 'btn primary', type: 'button' }, ['Mantener la red guardada']);
    const change = h('button', { class: 'btn ghost', type: 'button' }, ['Cambiar de Wi-Fi']);
    const choices = h('div', { class: 'form-row', hidden: !reusable }, [reuse, change]);
    const submit = h('button', { class: 'btn primary', type: 'submit', hidden: reusable }, [mode === 'add' ? 'Añadir a mi cuenta' : 'Conectar a esta red']);
    const close = h('button', { class: 'btn ghost', type: 'button' }, ['Cerrar']);
    const el = h('form', { class: 'form-stack wireless-form', 'aria-label': mode === 'add' ? 'Añadir aparato a mi cuenta' : 'Configurar la red del aparato' });
    if (code && screen) el.append(
      h('label', { class: 'field' }, [h('span', {}, ['Código que aparece en la pantalla de ASIS']), code]),
      h('p', { class: 'card-sub' }, ['Son 6 letras y números. Escríbelos tal y como los ves; el código cambia cada pocos minutos y solo sirve para esta unidad. La vinculación no activa el micrófono.']),
    );
    else if (code) el.append(
      h('label', { class: 'field' }, [h('span', {}, ['Código privado de alta de la etiqueta']), code]),
      h('p', { class: 'card-sub' }, ['Copia el código privado o el contenido del QR de tu unidad. Su nombre Bluetooth no basta para reclamarla. La vinculación no activa el micrófono.']),
    );
    // A factory unit's claim route does not read the vault; a unit already in the account or showing its screen code does.
    const savedOffered = (mode === 'configure' || screen) && typeof api.listWifiNetworks === 'function';
    const macBridge = options.macWifi !== undefined ? options.macWifi : (getMobileBridge() ? null : window.asisDesktop?.wifi ?? null);
    const useSaved = h('button', { class: 'btn ghost', type: 'button' }, ['Usar una de mis redes guardadas']);
    const useMac = h('button', { class: 'btn ghost', type: 'button' }, ['Usar la Wi-Fi de este Mac']);
    const savedRow = savedOffered || macBridge ? h('div', { class: 'form-row' }, [...(savedOffered ? [useSaved] : []), ...(macBridge ? [useMac] : [])]) : null;
    const savedHost = h('div', { class: 'form-stack', hidden: true });
    el.append(choices, fields, ...(savedRow ? [savedRow, savedHost] : []), error, h('div', { class: 'form-row' }, [submit, close]));
    const form: FormRef = { mode, el, code, ssid, psk, error, fields, choices, submit, saved: null, mac: null };
    const liveInfo = () => sync.getState().entries.find(item => item.key === entry.key)?.nearby?.info ?? null;
    const leaveSaved = () => {
      form.saved?.destroy?.(); form.saved = null; form.mac?.destroy?.(); form.mac = null; savedHost.replaceChildren(); savedHost.hidden = true;
      if (savedRow) savedRow.hidden = false; fields.hidden = false; choices.hidden = true; submit.hidden = false; ssid.focus();
    };
    const openPicker = (view: View) => {
      choices.hidden = true; fields.hidden = true; submit.hidden = true; if (savedRow) savedRow.hidden = true; savedHost.hidden = false;
      savedHost.append(view.el);
    };
    // The code stays in its field while a picker is open; it is read only when something is sent.
    const takeCode = (): string | undefined => { if (!code) return undefined; const value = code.value; code.value = ''; return value; };
    useSaved.addEventListener('click', () => {
      if (form.saved || form.mac || sync.getState().add.status === 'working') return;
      form.saved = createSavedNetworkChooser({
        api: api as Required<Pick<ApiClient, 'listWifiNetworks'>>,
        limit: () => deviceNetworkLimit(liveInfo() ?? {}),
        onSend: ids => { const possession = takeCode(); void sync.add({ deviceId: id, network: { savedNetworkIds: ids }, ...(possession !== undefined ? { possession } : {}) }); },
        onBack: leaveSaved,
      });
      openPicker(form.saved);
      form.saved.el.querySelector<HTMLElement>('input,button')?.focus();
    });
    useMac.addEventListener('click', () => {
      if (!macBridge || form.saved || form.mac || sync.getState().add.status === 'working') return;
      form.mac = createMacNetworkChooser({
        mac: macBridge,
        onSend: (name, key) => { const possession = takeCode(); void sync.add({ deviceId: id, network: { ssid: name, psk: key }, ...(possession !== undefined ? { possession } : {}) }); },
        onManual: name => { leaveSaved(); ssid.value = name; psk.focus(); },
        onBack: leaveSaved,
      });
      openPicker(form.mac);
    });
    el.addEventListener('submit', event => {
      event.preventDefault();
      if (sync.getState().add.status === 'working') return;
      const network = { ssid: ssid.value, psk: psk.value }; // Spaces are valid SSID characters; do not trim.
      const possession = code?.value ?? '';
      psk.value = ''; if (code) code.value = '';
      void sync.add({ deviceId: id, network, possession });
    });
    reuse.addEventListener('click', () => { if (sync.getState().add.status !== 'working') void sync.add({ deviceId: id, network: { reuse: true } }); });
    change.addEventListener('click', () => { choices.hidden = true; fields.hidden = false; submit.hidden = false; ssid.focus(); });
    close.addEventListener('click', () => { closeForm(ref); ref.add.focus(); });
    ref.form = form;
    (mode === 'add' ? ref.add : ref.configure).setAttribute('aria-expanded', 'true');
    ref.formHost.append(el);
    (reusable ? reuse : code ?? ssid).focus();
  }

  function updateInfo(ref: RowRef, entry: SyncEntry, state: SyncState): void {
    const account = entry.account;
    ref.info.hidden = !account || account.status === 'revoked';
    if (ref.info.hidden || !account) return;
    // A closed <details> still lays out its content; build it only while it is open.
    if (!ref.info.open) { if (ref.infoKey) { ref.infoBody.replaceChildren(); ref.infoKey = ''; } return; }
    const lines: Array<[string, string]> = [
      ['Nombre', account.name], ['Identificador', account.id], ['Vinculado', formatDate(account.pairedAt)],
      ['Último contacto', account.lastSeenAt ? `${formatDate(account.lastSeenAt)} (${agoText(clock() - Date.parse(account.lastSeenAt))})` : 'Todavía sin contacto con tu cuenta'],
      ['Actividad', account.status === 'online' ? 'Actividad reciente' : 'Sin actividad reciente'],
    ];
    const control = ref.control;
    // Without the controls component (member, shared unit, access not verified) nothing will ever load here.
    const noControls = ref.controls === null ? (ref.role === 'member' ? MEMBER_NO_CONTROLS : ref.remoteNotice.textContent || CHECKING_ACCESS) : null;
    lines.push(['Capacidades', noControls ?? (!control ? 'Cargando…' : !control.supported || !control.report ? 'El aparato aún no anuncia controles por Wi-Fi.'
      : ([control.report.capabilities.audio && 'audio', control.report.capabilities.wake && 'detección «Oye ASIS»', control.report.capabilities.camera && 'cámara'].filter(Boolean).join(', ') || 'ninguna anunciada'))]);
    lines.push(['Último estado de los controles', noControls ?? (!control ? 'Cargando…' : control.report ? `${reportLabel(control.report)}${control.reportedAt ? ` (${agoText(clock() - control.reportedAt)})` : ''}` : 'Todavía sin aviso del aparato.')]);
    const seen = state.bleSeen[account.id];
    if (seen) lines.push(['Wi-Fi guardada en el aparato', seen.source === 'receipt' ? 'Sí, confirmado al configurarlo en esta sesión.' : `${seen.hasWifi ? 'Sí' : 'No'}, leído por Bluetooth ${ago(seen.readAt)}.`]);
    const telemetry = account.telemetry;
    if (telemetry?.firmware) lines.push(['Firmware', telemetry.firmware]);
    if (telemetry?.flashBytes !== undefined) lines.push(['Memoria flash', formatBytes(telemetry.flashBytes)]);
    if (telemetry?.psramBytes !== undefined) lines.push(['PSRAM', formatBytes(telemetry.psramBytes)]);
    if (telemetry?.sd) lines.push(['Tarjeta SD', telemetry.sd.available ? `Disponible${telemetry.sd.capacityBytes !== undefined ? ` · ${formatBytes(telemetry.sd.capacityBytes)}` : ''}` : 'No detectada']);
    const missingTelemetry = !telemetry?.firmware || telemetry.flashBytes === undefined || telemetry.psramBytes === undefined || !telemetry.sd;
    const needsUsb = missingTelemetry && usbAvailable;
    if (missingTelemetry && !usbAvailable) lines.push(['Firmware, memoria y tarjeta SD', 'Solo se leen por USB desde ASIS para Mac.']);
    const key = JSON.stringify([lines, needsUsb]);
    if (key === ref.infoKey) return;
    ref.infoKey = key;
    const dl = h('dl', { class: 'device-info-list' });
    for (const [label, value] of lines) dl.append(h('dt', {}, [label]), h('dd', {}, [value]));
    if (needsUsb) {
      const open = h('button', { class: 'btn ghost small', type: 'button' }, ['Abrir diagnóstico USB']);
      open.addEventListener('click', () => options.openUsb('diagnose'));
      dl.append(h('dt', {}, ['Firmware, memoria y tarjeta SD']), h('dd', {}, [h('span', {}, ['Disponible por USB. ']), open]));
    }
    ref.infoBody.replaceChildren(dl);
  }

  function refreshInfo(ref: RowRef): void {
    const live = sync.getState();
    const current = live.entries.find(item => item.key === ref.key);
    if (alive && current) updateInfo(ref, current, live);
  }

  function mountControls(ref: RowRef, deviceId: string): void {
    ref.controls = createDeviceControls({ api, deviceId, onControl: control => { ref.control = control; refreshInfo(ref); updatePresence(ref); } });
    ref.row.append(ref.controls.el);
    const camera = ref.controls.el.querySelector('.secondary-details');
    if (camera) ref.advanced.append(camera);
  }
  function unmountControls(ref: RowRef): void {
    ref.advanced.querySelector('.secondary-details')?.remove();
    ref.controls?.destroy?.(); ref.controls?.el.remove(); ref.controls = null; ref.control = null; updatePresence(ref);
  }
  function teardownAccess(ref: RowRef): void {
    unmountControls(ref);
    ref.members?.destroy?.(); ref.members?.el.remove(); ref.members = null;
  }
  /** Remote controls stay off until the roster proves the unit is not shared; a failed check keeps them off. */
  function mountMembers(ref: RowRef, deviceId: string, role: DeviceRole): void {
    ref.members = createDeviceMembers({
      api, deviceId, role, currentUserId: options.currentUserId ?? '', preload: role === 'owner',
      onChange: () => { if (alive) void sync.refreshAccount({ silent: true }); },
      onAccess: shared => {
        const device = sync.getState().account.devices.find(item => item.id === deviceId);
        if (!alive || ref.role !== 'owner' || !device || device.status === 'revoked') return;
        ref.remoteAllowed = shared === false;
        ref.remoteNotice.hidden = ref.remoteAllowed;
        ref.remoteNotice.textContent = shared === true ? SHARED_NO_CONTROLS : UNVERIFIED_ACCESS;
        if (!ref.remoteAllowed) unmountControls(ref);
        else if (!ref.controls) mountControls(ref, deviceId);
        refreshInfo(ref);
      },
    });
    ref.advanced.append(ref.members.el);
  }

  function updatePresence(ref: RowRef): void {
    const account = sync.getState().entries.find(entry => entry.key === ref.key)?.account;
    const report = ref.control?.report;
    const recent = account?.status === 'online' && ref.control?.reportedAt != null && clock() - ref.control.reportedAt < 90_000;
    const state = !account ? 'nearby' : account.status === 'revoked' ? 'revoked' : account?.status !== 'online' ? 'offline' : !recent || !report ? 'unknown' : report.speaking ? 'speaking' : report.capturing ? 'listening' : 'ready';
    ref.portrait.dataset.state = state;
    ref.activity.textContent = state === 'nearby' ? 'Cerca · sin añadir' : state === 'revoked' ? 'Unidad revocada' : state === 'offline' ? 'Sin conexión' : state === 'unknown' ? 'Conectada · estado sin confirmar' : state === 'speaking' ? 'Hablando' : state === 'listening' ? 'Escuchando' : 'Lista';
  }

  function updateRow(ref: RowRef, entry: SyncEntry, state: SyncState, fresh: boolean, revisionChanged: boolean): void {
    const account = entry.account;
    const busy = state.add.status === 'working' || state.phase === 'searching';
    const role: DeviceRole = account?.role ?? 'owner';
    ref.row.className = `device-row ${account ? `device-${account.status} device-role-${role}` : 'device-candidate'}`;
    mutate(ref.name, entry.name);
    updatePresence(ref);
    mutate(ref.sub, account ? (account.lastSeenAt ? `Último contacto: ${formatDate(account.lastSeenAt)}` : 'Esperando el primer contacto') : entry.id ? `ID: ${entry.id}` : entry.nearby ? 'Aparato Bluetooth cercano' : 'Conexión por cable');
    const chipsKey = entry.chips.map(chip => `${chip.id}:${chip.text}`).join('|');
    if (chipsKey !== ref.chipsKey) {
      ref.chipsKey = chipsKey;
      ref.chips.replaceChildren(...entry.chips.map(chip => h('span', { class: `tag tag-${chip.id}` }, [chip.text])));
    }
    const note = entry.nearby?.selecting ? 'Conectando con el aparato elegido…' : entry.note ?? '';
    mutate(ref.note, note); ref.note.hidden = !note;
    // Only the owner renames or revokes; a member of a shared unit sees neither.
    const owner = Boolean(account) && role === 'owner';
    ref.actions.hidden = !owner;
    ref.rename.hidden = ref.revoke.hidden = !account;
    ref.rename.disabled = ref.revoke.disabled = !owner || account?.status === 'revoked';
    ref.release.hidden = ref.release.disabled = !account || !owner || account.status === 'revoked';
    ref.release.setAttribute('aria-label', `Liberar ${entry.name}`);
    ref.rename.setAttribute('aria-label', `Renombrar ${entry.name}`); ref.revoke.setAttribute('aria-label', `Revocar ${entry.name}`);
    const labels = { add: `Añadir ${entry.name} a mi cuenta`, configure: `Configurar Wi-Fi de ${entry.name}`, connect: `Conectar con ${entry.name}`, 'add-usb': `Añadir por USB ${entry.name}` } as const;
    for (const [button, action] of [[ref.add, 'add'], [ref.configure, 'configure'], [ref.connect, 'connect'], [ref.usb, 'add-usb']] as const) {
      button.hidden = !entry.actions.includes(action);
      button.disabled = action === 'connect' ? Boolean(entry.nearby?.selecting) : busy;
      button.setAttribute('aria-label', labels[action]);
      if (action === 'add' || action === 'configure') button.setAttribute('aria-expanded', String(ref.form?.mode === action));
    }
    ref.syncActions.hidden = ref.add.hidden && ref.configure.hidden && ref.connect.hidden && ref.usb.hidden;
    // The form belongs to an offer that still exists; losing the radio link discards typed secrets.
    const form = ref.form;
    if (form) {
      const stillOffered = entry.actions.includes(form.mode);
      if (!stillOffered && !(state.add.status === 'working' && state.add.deviceId === entry.id)) closeForm(ref);
      else {
        const message = state.add.status === 'invalid' && state.add.deviceId === entry.id ? state.add.message ?? '' : '';
        mutate(form.error, message); form.error.hidden = !message;
        for (const control of form.el.querySelectorAll<HTMLInputElement | HTMLButtonElement>('input,button')) control.disabled = busy;
        form.saved?.refresh(busy);
      }
    }
    // Access: remote controls and the shared-device roster. Both are built once per unit and survive
    // refreshes (consent, receipts and focus); a role change rebuilds them so nothing is inherited.
    if (account && account.status !== 'revoked') {
      const grow = revisionChanged && !fresh;
      if (ref.role !== role) {
        const rebuild = ref.role !== null;
        if (rebuild) teardownAccess(ref);
        ref.role = role;
        ref.remoteAllowed = !hasMembers;
        ref.remoteNotice.hidden = role !== 'owner' || ref.remoteAllowed;
        ref.remoteNotice.textContent = CHECKING_ACCESS;
        if (role === 'owner' && ref.remoteAllowed) mountControls(ref, account.id);
        if (hasMembers) mountMembers(ref, account.id, role);
      } else {
        if (!hasMembers) ref.remoteAllowed = true;
        if (!ref.controls && role === 'owner' && ref.remoteAllowed) mountControls(ref, account.id);
        else if (role !== 'owner' && ref.controls) unmountControls(ref);
        else if (ref.controls && grow) ref.controls.update?.({});
        if (!ref.members && hasMembers) mountMembers(ref, account.id, role);
        else if (grow) ref.members?.update?.({});
      }
    } else if (ref.role !== null || ref.controls || ref.members) {
      teardownAccess(ref);
      ref.role = null; ref.remoteAllowed = false; ref.remoteNotice.hidden = true;
    }
    if (entry.added && !ref.added) { ref.added = true; ref.info.open = true; ref.focusPending = true; }
    else if (!entry.added) ref.added = false;
    updateInfo(ref, entry, state);
  }

  function destroyRow(ref: RowRef): void {
    closeForm(ref);
    teardownAccess(ref);
    ref.row.remove();
  }

  function render(state: SyncState): void {
    if (!alive) return;
    const busy = state.phase === 'searching' || state.phase === 'adding';
    const syncFocused = document.activeElement === syncButton, cancelFocused = document.activeElement === cancelButton;
    syncButton.disabled = busy;
    cancelButton.hidden = !busy;
    if (busy && !wasBusy && syncFocused) cancelButton.focus();
    if (!busy && wasBusy && cancelFocused) syncButton.focus();
    wasBusy = busy;
    list.setAttribute('aria-busy', state.account.status === 'loading' ? 'true' : 'false');
    if (state.message !== statusText) { statusText = state.message; status.textContent = state.message; }
    status.hidden = !state.synced && !busy;
    mutate(alertText, state.alert ?? ''); alertText.hidden = !state.alert;
    retryButton.hidden = state.account.status !== 'error';
    steps.hidden = !state.synced;
    if (state.synced) {
      const text = describeSources(state);
      for (const [key, label] of [['account', 'Cuenta'], ['ble', 'Bluetooth'], ['usb', 'USB']] as const) {
        const next = `${label}: ${text[key]}`; mutate(stepItems[key], next);
      }
    }
    const bootHint = ['searching', 'none', 'expired', 'disconnected', 'error'].includes(state.ble.status) && state.synced;
    const hintText = state.synced && state.ble.status === 'unavailable' ? NO_BLUETOOTH : bootHint ? BOOT_HINT : '';
    mutate(hint, hintText); hint.hidden = !hintText;

    const keys = new Set(state.entries.map(entry => entry.key));
    for (const [key, ref] of rows) if (!keys.has(key)) { destroyRow(ref); rows.delete(key); }
    const revisionChanged = state.account.revision !== lastRevision;
    lastRevision = state.account.revision;
    if (!state.entries.length) {
      // On a failed load the alert above already says why; an empty-state sentence would contradict it.
      if (state.account.status === 'error') { list.replaceChildren(); return; }
      placeholder.textContent = state.account.status === 'loading' || state.account.status === 'idle' ? 'Cargando…'
        : 'Aún no hay dispositivos vinculados. Pulsa Sincronizar para buscar uno.';
      list.replaceChildren(placeholder);
      return;
    }
    if (placeholder.isConnected) placeholder.remove();
    state.entries.forEach((entry, index) => {
      let ref = rows.get(entry.key);
      const fresh = !ref;
      if (!ref) { ref = createRow(entry); rows.set(entry.key, ref); }
      updateRow(ref, entry, state, fresh, revisionChanged);
      const atIndex = list.children.item(index);
      if (atIndex !== ref.row) {
        const focused = document.activeElement instanceof HTMLElement && ref.row.contains(document.activeElement) ? document.activeElement : null;
        list.insertBefore(ref.row, atIndex);
        if (focused?.isConnected && document.activeElement !== focused) focused.focus({ preventScroll: true });
      }
    });
    // The submit button that had focus is gone; land on the row that now shows the unit.
    for (const ref of rows.values()) {
      if (!ref.focusPending || !ref.row.isConnected) continue;
      ref.focusPending = false;
      const active = document.activeElement;
      if (!active || active === document.body || !active.isConnected || ref.row.contains(active)) { ref.row.focus(); ref.row.scrollIntoView?.({ block: 'nearest' }); }
    }
  }

  syncButton.addEventListener('click', () => { void sync.start(); });
  invitationButton?.addEventListener('click', () => { options.onInvitation?.(); });
  cancelButton.addEventListener('click', () => { sync.cancel(); });
  retryButton.addEventListener('click', () => { void sync.refreshAccount(); });
  const leave = () => sync.cancel();
  window.addEventListener('pagehide', leave);
  window.addEventListener('beforeunload', leave);
  const unsubscribe = sync.subscribe(render);
  render(sync.getState());
  const presenceClock = setInterval(() => { for (const ref of rows.values()) updatePresence(ref); }, 15_000);

  return {
    el: root,
    destroy() {
      alive = false; unsubscribe(); clearInterval(presenceClock);
      window.removeEventListener('pagehide', leave); window.removeEventListener('beforeunload', leave);
      for (const ref of rows.values()) destroyRow(ref);
      rows.clear();
    },
  };
}
